/**
 * TK Infinite Scroll
 *
 * Appends the next paginated page of products into the grid as the visitor
 * approaches the end of the list, on search results and any other template
 * that renders `.product-grid-container` together with `.pagination`.
 *
 * The pagination links are left untouched in the markup and are only hidden
 * once this script has taken over, so crawlers and visitors without JS keep
 * the numbered pages. History is kept in sync with `?page=`, which means a
 * reload or a shared link lands on a page the visitor actually reached.
 */
(function () {
  'use strict';

  /* The search page renders .tk-prod-list; other listings still use the
     older .product-grid-container. Match either so one script covers both. */
  var GRID = '.tk-prod-list, .product-grid-container';
  var PAGINATION = '.pagination';
  var ITEM = '.tk-card, .grid__item';
  /* Start fetching this far before the sentinel scrolls into view. */
  var ROOT_MARGIN = '600px';
  /* Auto-load this many pages before asking for a click, so a visitor who
     scrolls to the bottom of a 1000-result search does not silently pull
     down eighty pages of markup. */
  var AUTO_PAGE_LIMIT = 4;

  function text(el) {
    return (el && el.textContent ? el.textContent : '').trim();
  }

  function nextUrlFrom(root) {
    var pagination = root.querySelector(PAGINATION);
    if (!pagination) return null;
    /* The theme marks the next link with .next; fall back to matching the
       label so a translated or restyled pagination still works. */
    var link = pagination.querySelector('.next a[href]');
    if (!link) {
      var links = pagination.querySelectorAll('a[href*="page="]');
      for (var i = 0; i < links.length; i++) {
        if (/next|›|»|→/i.test(text(links[i]))) { link = links[i]; break; }
      }
    }
    return link ? link.getAttribute('href') : null;
  }

  function init(container) {
    if (!container || container.dataset.tkInfiniteReady === 'true') return;

    var grid = container.querySelector(GRID);
    var pagination = container.querySelector(PAGINATION);
    if (!grid || !pagination) return;

    var nextUrl = nextUrlFrom(container);
    if (!nextUrl) return;

    container.classList.add('tk-infinite');
    container.dataset.tkInfiniteReady = 'true';

    var strings = {
      loading: container.dataset.tkInfiniteLoading || 'Loading more products',
      more: container.dataset.tkInfiniteMore || 'Load more products',
      end: container.dataset.tkInfiniteEnd || 'You have reached the end of the results',
      error: container.dataset.tkInfiniteError || 'Could not load more products'
    };

    /* One live region announces loading, errors and the end of the list, so a
       screen reader hears what the visual spinner shows. */
    var status = document.createElement('div');
    status.className = 'tk-infinite__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    pagination.parentNode.insertBefore(status, pagination);

    var sentinel = document.createElement('div');
    sentinel.className = 'tk-infinite__sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    status.parentNode.insertBefore(sentinel, status);

    var loading = false;
    var finished = false;
    var pagesLoaded = 0;

    function showSpinner() {
      status.hidden = false;
      status.innerHTML = '';
      var spinner = document.createElement('span');
      spinner.className = 'tk-infinite__spinner';
      var label = document.createElement('span');
      label.textContent = strings.loading;
      status.appendChild(spinner);
      status.appendChild(label);
    }

    function showMessage(message) {
      status.hidden = false;
      status.textContent = message;
    }

    function showButton(message) {
      status.hidden = false;
      status.innerHTML = '';
      if (message) {
        var note = document.createElement('span');
        note.textContent = message;
        status.appendChild(note);
      }
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'tk-infinite__button';
      button.textContent = strings.more;
      button.addEventListener('click', function () {
        /* A manual click resets the auto budget for the next stretch. */
        pagesLoaded = 0;
        load();
      });
      status.appendChild(button);
    }

    function finish() {
      finished = true;
      if (observer) observer.disconnect();
      showMessage(strings.end);
    }

    function load() {
      if (loading || finished || !nextUrl) return;
      /* Past the auto budget the visitor asks for each further page. */
      if (pagesLoaded >= AUTO_PAGE_LIMIT) { showButton(''); return; }
      loading = true;
      showSpinner();

      var requestedUrl = nextUrl;

      fetch(requestedUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var nextGrid = parsed.querySelector(GRID);
          if (!nextGrid) throw new Error('grid missing in response');

          var items = nextGrid.querySelectorAll(ITEM);
          if (!items.length) { finish(); return; }

          var fragment = document.createDocumentFragment();
          for (var i = 0; i < items.length; i++) {
            fragment.appendChild(document.importNode(items[i], true));
          }
          grid.appendChild(fragment);

          /* Judge.me renders its badges once on load; ask it to pick up the
             rows we just added, if the app is present. */
          if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
            try { window.jdgm.customizeBadges(); } catch (e) {}
          }

          /* Keep the address bar on the page the visitor is actually reading. */
          try { history.replaceState(history.state, '', requestedUrl); } catch (e) {}

          pagesLoaded++;
          nextUrl = nextUrlFrom(parsed);
          loading = false;

          if (!nextUrl) { finish(); return; }
          if (pagesLoaded >= AUTO_PAGE_LIMIT) { showButton(''); return; }
          status.hidden = true;
        })
        .catch(function () {
          loading = false;
          showButton(strings.error);
        });
    }

    var observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { load(); break; }
        }
      }, { rootMargin: ROOT_MARGIN });
      observer.observe(sentinel);

      /* A jump straight to the end of the page (End key, scrollbar drag, or a
         restored scroll position) can leave the sentinel already above the
         viewport, where it never intersects again. Check its position on
         scroll as well so those visitors still get the next page. */
      var checking = false;
      window.addEventListener('scroll', function () {
        if (checking || loading || finished) return;
        checking = true;
        window.requestAnimationFrame(function () {
          checking = false;
          if (loading || finished) return;
          var rect = sentinel.getBoundingClientRect();
          if (rect.top <= window.innerHeight + parseInt(ROOT_MARGIN, 10)) load();
        });
      }, { passive: true });
    } else {
      /* Without IntersectionObserver the visitor still gets every page, one
         button press at a time. */
      showButton('');
    }
  }

  function boot() {
    var containers = document.querySelectorAll('[data-tk-infinite]');
    for (var i = 0; i < containers.length; i++) init(containers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
